import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from '@dhl-ui/icons'
import { TextField, IconButton } from '@dhl-ui/components'
import { useDebounce } from '@dhl/utils'
import type { Meta } from '~/api/usePaginatedAsyncCall'
import { useSearchQuery } from '~/common/hooks'

type TransformFn = (value: string) => string
type Props = {
  meta?: Meta
  onChange?: (search: string) => void
  debounceDelayMsec?: number
  withoutSearchParams?: boolean
  transform?: TransformFn // function to be applied on the search term when the search term is changed
}

export const noTransform: TransformFn = (s) => s
export const lowercaseTransform: TransformFn = (s) => s.toLowerCase()

export const SearchTextField = ({
  meta,
  onChange,
  debounceDelayMsec = 300,
  withoutSearchParams = false,
  transform = noTransform,
}: Props) => {
  const { t } = useTranslation()
  const searchParam = useSearchQuery()
  const [search, setSearch] = useState<string>(withoutSearchParams ? '' : searchParam)
  const debouncedSearch = useDebounce(search, debounceDelayMsec)
  const isSearchKeyPressed = useRef(false)

  useEffect(() => {
    if (searchParam.length > 0 && !withoutSearchParams) {
      handleSearch(searchParam)
    }
  }, [searchParam, withoutSearchParams])

  useEffect(() => {
    if (meta && meta.query !== debouncedSearch) {
      meta.setQuery(debouncedSearch)
      meta.setPage(0)
    }
  }, [debouncedSearch, meta])

  useEffect(() => {
    if (onChange && isSearchKeyPressed.current) {
      onChange(debouncedSearch)
    }
  }, [debouncedSearch, onChange])

  const handleSearch = (value: string) => {
    setSearch(transform(value))
    isSearchKeyPressed.current = true
  }

  return (
    <TextField
      data-testid="search-input"
      value={search}
      slotProps={{
        input: {
          endAdornment: search ? (
            <IconButton
              disabled={!search}
              onClick={() => handleSearch('')}
              edge="end"
              aria-label={t('components.seachTextField.clearSearchButtonAriaLabel')}
              data-testid="clear-search-button"
              size="large"
            >
              <CloseIcon color="action" />
            </IconButton>
          ) : null,
        },
      }}
      onChange={({ target: { value } }) => handleSearch(value)}
      placeholder={t('admin.searchText')}
    />
  )
}
